/**
 * GDPR Re-consent Confirmation Page (Phase 2, Task #176)
 *
 * Public, no-auth page reached from the email link sent by the daily
 * gdpr-consent-check job. URL format:
 *   /gdpr/consent?choice=keep&token=<hmac>
 *   /gdpr/consent?choice=delete&token=<hmac>
 *
 * Flow: parse query params -> show confirmation card -> user clicks confirm
 * -> POST /api/gdpr/consent-response -> success / error message.
 *
 * The user is NEVER auto-submitted on page load — a deliberate confirmation
 * click is required so an email-link prefetcher (Outlook, corporate proxies)
 * can't accidentally consume the single-use nonce.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, Trash2, ShieldCheck } from 'lucide-react';

type Choice = 'keep' | 'delete';

interface ApiResult {
  success: boolean;
  message?: string;
  errorCode?: string;
}

function parseSearchParams(): { choice: Choice | null; token: string | null } {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);
  const rawChoice = params.get('choice');
  const choice: Choice | null = rawChoice === 'keep' || rawChoice === 'delete' ? rawChoice : null;
  return { choice, token: params.get('token') };
}

export default function GdprConsentPage() {
  const [, setLocation] = useLocation();
  const [{ choice, token }, setParams] = useState(parseSearchParams);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ApiResult | null>(null);

  useEffect(() => {
    // Re-read on mount in case wouter strips the query — defensive only.
    setParams(parseSearchParams());
  }, []);

  const missingParams = !choice || !token;

  const handleConfirm = async () => {
    if (missingParams || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/gdpr/consent-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, choice }),
      });
      const body = (await res.json().catch(() => ({}))) as ApiResult;
      setResult({
        success: !!body.success && res.ok,
        message: body.message,
        errorCode: body.errorCode,
      });
    } catch (err) {
      setResult({
        success: false,
        message: 'We could not reach our servers. Please check your connection and try again.',
        errorCode: 'network_error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {choice === 'delete' ? (
              <Trash2 className="h-5 w-5 text-destructive" data-testid="icon-delete" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-primary" data-testid="icon-keep" />
            )}
            {choice === 'delete' ? 'Delete your data' : 'Keep your data'}
          </CardTitle>
          <CardDescription>
            talenture.ai is following up on your account because we have not seen you in a while.
            Per GDPR, please confirm what you would like us to do with your data.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {missingParams && (
            <Alert variant="destructive" data-testid="alert-missing-params">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>This link is incomplete</AlertTitle>
              <AlertDescription>
                The link you followed is missing required information. Please use the most recent
                email we sent you, or contact{' '}
                <a className="underline" href="mailto:support@talenture.ai">
                  support@talenture.ai
                </a>
                .
              </AlertDescription>
            </Alert>
          )}

          {!missingParams && !result && (
            <>
              <p className="text-sm text-muted-foreground">
                {choice === 'delete' ? (
                  <>
                    You are about to <strong>request deletion</strong> of your candidate profile,
                    CV, applications and interview history. Our team will action your request
                    within 30 days, in line with GDPR Article 17.
                  </>
                ) : (
                  <>
                    You are about to <strong>renew your consent</strong> so we can keep your
                    profile and matches active for another <strong>24 months</strong>. We will
                    check in again before that period ends.
                  </>
                )}
              </p>

              <Button
                className="w-full"
                variant={choice === 'delete' ? 'destructive' : 'default'}
                disabled={submitting}
                onClick={handleConfirm}
                data-testid="button-confirm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : choice === 'delete' ? (
                  'Confirm deletion'
                ) : (
                  'Confirm — keep my data'
                )}
              </Button>
            </>
          )}

          {result?.success && (
            <Alert data-testid="alert-success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Thank you</AlertTitle>
              <AlertDescription>{result.message ?? 'Your response has been recorded.'}</AlertDescription>
            </Alert>
          )}

          {result && !result.success && (
            <Alert variant="destructive" data-testid="alert-error">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {result.errorCode === 'token_expired'
                  ? 'Link expired'
                  : result.errorCode === 'token_already_used'
                  ? 'Link already used'
                  : 'We could not record your response'}
              </AlertTitle>
              <AlertDescription>
                {result.message ??
                  'Please contact support@talenture.ai if you continue to see this error.'}
              </AlertDescription>
            </Alert>
          )}

          {result && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setLocation('/')}
              data-testid="button-home"
            >
              Return to talenture.ai
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
